import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Button, Card, Col, DatePicker, Empty, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Skeleton, Space, Table, Tag, Typography,
} from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, DownloadOutlined, EditOutlined,
  PaperClipOutlined, PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCashAccount, useCashAccounts, useCashCategories, useCashTransactions, useCounterparties,
  useCreateCashAccount, useUpdateCashAccount, useVoidCashTransaction,
  type CashAccount, type CashAccountKind, type CashDirection, type CashTransaction,
  type MethodBalances, type PaymentMethod,
} from '../api/cash';
import { TransactionModal } from '../components/transaction-modal';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';
import { presignedDownload } from '@/features/customers/api/customers';
import { downloadFromApi } from '@/shared/lib/download';
import { useHasRole, ADMIN_ROLES, ACCOUNTANT_ROLES } from '@/shared/auth/role';

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'card'];

export function KassaPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: accounts, isLoading } = useCashAccounts();
  const isAdmin = useHasRole(ADMIN_ROLES);
  const [accountModal, setAccountModal] = useState<{ mode: 'create' | 'edit'; account?: CashAccount } | null>(null);

  // Auto-select primary / first account when none is in the URL.
  useEffect(() => {
    if (id || !accounts?.length) return;
    const primary = accounts.find((a) => a.isPrimary && a.active) ?? accounts[0];
    if (primary) navigate(`/kassa/${primary.id}`, { replace: true });
  }, [id, accounts, navigate]);

  const total = (accounts ?? []).reduce((acc, a) => acc + BigInt(a.balance), 0n);

  return (
    <Row gutter={16} style={{ alignItems: 'flex-start' }}>
      {/* Left: account sidebar */}
      <Col xs={24} md={7} lg={6} style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {isAdmin && (
            <Button type="primary" block icon={<PlusOutlined />} onClick={() => setAccountModal({ mode: 'create' })}>
              {t('kassa.newAccount')}
            </Button>
          )}
          <Card bodyStyle={{ padding: '10px 14px' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('kassa.totalBalance')}</Typography.Text>
            <Typography.Title level={4} style={{ margin: 0, color: '#16a34a' }}>{formatUzs(total)}</Typography.Title>
          </Card>
          {isLoading ? (
            <Skeleton active />
          ) : (
            (accounts ?? []).map((a) => (
              <AccountCard
                key={a.id}
                account={a}
                selected={a.id === id}
                isAdmin={isAdmin}
                onSelect={() => navigate(`/kassa/${a.id}`)}
                onEdit={() => setAccountModal({ mode: 'edit', account: a })}
              />
            ))
          )}
        </Space>
      </Col>

      {/* Right: selected account ledger */}
      <Col xs={24} md={17} lg={18}>
        {id ? <Ledger accountId={id} /> : <Card><Empty description={t('kassa.selectAccount')} /></Card>}
      </Col>

      {accountModal && (
        <AccountFormModal
          mode={accountModal.mode}
          account={accountModal.account}
          onClose={() => setAccountModal(null)}
        />
      )}
    </Row>
  );
}

function AccountCard({
  account, selected, isAdmin, onSelect, onEdit,
}: {
  account: CashAccount;
  selected: boolean;
  isAdmin: boolean;
  onSelect: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const [modal, setModal] = useState<CashDirection | null>(null);
  const canTxn = useHasRole(ACCOUNTANT_ROLES);

  return (
    <>
      <Card
        hoverable
        onClick={onSelect}
        bodyStyle={{ padding: 14 }}
        style={{
          cursor: 'pointer',
          background: selected ? '#2563eb' : '#f1f5f9',
          border: selected ? '1px solid #2563eb' : '1px solid #e2e8f0',
          opacity: account.active ? 1 : 0.6,
        }}
      >
        <Typography.Title level={4} style={{ margin: 0, color: selected ? '#fff' : '#0f172a' }}>
          {formatUzs(account.balance)}
        </Typography.Title>
        <div style={{ color: selected ? 'rgba(255,255,255,0.92)' : '#334155', fontWeight: 500, marginTop: 2 }}>
          {account.name}
        </div>
        <div style={{ color: selected ? 'rgba(255,255,255,0.7)' : '#94a3b8', fontSize: 12 }}>
          {t(`kassa.kinds.${account.kind}`)}
          {account.isPrimary ? ` · ${t('kassa.primary')}` : ''}
        </div>

        {selected && (
          <Space size={6} style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()} wrap>
            {isAdmin && (
              <Button size="small" shape="circle" icon={<EditOutlined />} onClick={onEdit} />
            )}
            {canTxn && (
              <>
                <Button size="small" style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff' }} icon={<ArrowDownOutlined />} onClick={() => setModal('income')}>
                  {t('kassa.income')}
                </Button>
                <Button size="small" style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff' }} icon={<ArrowUpOutlined />} onClick={() => setModal('expense')}>
                  {t('kassa.expense')}
                </Button>
              </>
            )}
          </Space>
        )}
      </Card>

      {modal && (
        <TransactionModal open direction={modal} cashAccountId={account.id} onClose={() => setModal(null)} />
      )}
    </>
  );
}

function MethodChips({ balances }: { balances: MethodBalances }) {
  const { t } = useTranslation();
  const chips = METHODS.map((m) => ({ m, val: BigInt(balances[m]) })).filter((c) => c.val !== 0n);
  if (chips.length === 0) return null;
  return (
    <Space wrap>
      {chips.map(({ m, val }) => (
        <div key={m} style={{ background: '#1e3a5f', borderRadius: 8, padding: '6px 14px', minWidth: 120 }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>{t(`apartments.paymentMethods.${m}`)}</div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{formatUzs(val.toString())}</div>
        </div>
      ))}
    </Space>
  );
}

function Ledger({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data: account, isLoading } = useCashAccount(accountId);
  const { data: categories } = useCashCategories();
  const { data: counterparties } = useCounterparties();
  const voidTxn = useVoidCashTransaction();

  const [direction, setDirection] = useState<'' | CashDirection>('');
  const [method, setMethod] = useState<'' | PaymentMethod>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [counterpartyId, setCounterpartyId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const { data: txns, isLoadingAll } = useCashTransactions({
    cashAccountId: accountId,
    direction: direction || undefined,
    method: method || undefined,
    categoryId: categoryId || undefined,
    counterpartyId: counterpartyId || undefined,
    search: search.trim() || undefined,
    dateFrom: range?.[0]?.format('YYYY-MM-DD'),
    dateTo: range?.[1]?.format('YYYY-MM-DD'),
  });

  const categoryName = useMemo(() => {
    const map = new Map((categories ?? []).map((c) => [c.id, c.name]));
    return (cid: string) => map.get(cid) ?? '';
  }, [categories]);

  if (isLoading) return <Card><Skeleton active /></Card>;
  if (!account) return <Card><Empty /></Card>;

  async function openFile(key: string) {
    try {
      window.open(await presignedDownload(key), '_blank', 'noopener,noreferrer');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function exportCsv() {
    const params = new URLSearchParams({ cashAccountId: accountId });
    if (direction) params.set('direction', direction);
    if (method) params.set('method', method);
    if (categoryId) params.set('categoryId', categoryId);
    if (counterpartyId) params.set('counterpartyId', counterpartyId);
    if (search.trim()) params.set('search', search.trim());
    if (range?.[0]) params.set('dateFrom', range[0].format('YYYY-MM-DD'));
    if (range?.[1]) params.set('dateTo', range[1].format('YYYY-MM-DD'));
    try {
      await downloadFromApi(`/cash-transactions/export.csv?${params.toString()}`, 'kassa.csv');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {account.methodBalances && <MethodChips balances={account.methodBalances} />}

      <Card bodyStyle={{ padding: 12 }}>
        <Space wrap size={[16, 8]}>
          <span>
            <Typography.Text type="secondary" style={{ marginRight: 6 }}>{t('kassa.direction')}:</Typography.Text>
            <Select
              allowClear style={{ width: 160 }} value={direction || undefined} onChange={(v) => setDirection(v ?? '')}
              placeholder={t('kassa.direction')}
              options={[{ value: 'income', label: t('kassa.income') }, { value: 'expense', label: t('kassa.expense') }]}
            />
          </span>
          <span>
            <Typography.Text type="secondary" style={{ marginRight: 6 }}>{t('kassa.method')}:</Typography.Text>
            <Select
              allowClear style={{ width: 180 }} value={method || undefined} onChange={(v) => setMethod(v ?? '')}
              placeholder={t('kassa.method')}
              options={METHODS.map((m) => ({ value: m, label: t(`apartments.paymentMethods.${m}`) }))}
            />
          </span>
          <span>
            <Typography.Text type="secondary" style={{ marginRight: 6 }}>{t('kassa.category')}:</Typography.Text>
            <Select
              allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
              value={categoryId || undefined} onChange={(v) => setCategoryId(v ?? '')}
              placeholder={t('kassa.category')}
              options={(categories ?? [])
                .filter((c) => !direction || c.scope === direction || c.scope === 'both')
                .map((c) => ({ value: c.id, label: c.name }))}
            />
          </span>
          <span>
            <Typography.Text type="secondary" style={{ marginRight: 6 }}>{t('kassa.counterparty')}:</Typography.Text>
            <Select
              allowClear showSearch optionFilterProp="label" style={{ width: 200 }}
              value={counterpartyId || undefined} onChange={(v) => setCounterpartyId(v ?? '')}
              placeholder={t('kassa.counterparty')}
              options={(counterparties ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </span>
          <span>
            <Typography.Text type="secondary" style={{ marginRight: 6 }}>{t('kassa.date')}:</Typography.Text>
            <DatePicker.RangePicker format="DD.MM.YYYY" value={range} onChange={(v) => setRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
          </span>
          <Input.Search
            allowClear style={{ width: 220 }} placeholder={t('kassa.searchPlaceholder')}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>CSV</Button>
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<CashTransaction>
          rowKey="id"
          dataSource={txns ?? []}
          loading={isLoadingAll}
          pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          rowClassName={(r) => (r.voidedAt ? 'kassa-voided-row' : '')}
          columns={[
            { title: t('kassa.date'), dataIndex: 'txnDate', width: 120, render: (v: string) => dayjs(v).format('DD.MM.YYYY') },
            {
              title: t('kassa.transactionType'),
              dataIndex: 'direction',
              width: 110,
              render: (d: CashDirection) => <Tag color={d === 'income' ? 'green' : 'red'}>{t(`kassa.${d}`)}</Tag>,
            },
            {
              title: t('kassa.amount'),
              width: 200,
              render: (_: unknown, r: CashTransaction) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text
                    strong
                    style={{
                      color: r.excludeFromBalance ? '#94a3b8' : r.direction === 'income' ? '#16a34a' : '#dc2626',
                      textDecoration: r.voidedAt ? 'line-through' : 'none',
                    }}
                  >
                    {r.direction === 'income' ? '+' : '−'}{formatUzs(r.amount)}
                  </Typography.Text>
                  {r.excludeFromBalance && !r.voidedAt && (
                    <Tag color="default" style={{ fontSize: 11, marginInlineEnd: 0 }}>
                      {t('kassa.excludedFromBalance')}
                    </Tag>
                  )}
                </Space>
              ),
            },
            {
              title: t('kassa.note'),
              render: (_: unknown, r: CashTransaction) => (
                <div>
                  <div>{r.description || '—'}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {categoryName(r.categoryId)}{r.counterparty ? ` · ${r.counterparty.name}` : ''}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: '',
              width: 80,
              render: (_: unknown, r: CashTransaction) => (
                <Space size={4}>
                  {r.attachmentUrls.length > 0 && (
                    <Button size="small" icon={<PaperClipOutlined />} onClick={() => void openFile(r.attachmentUrls[0]!)} />
                  )}
                  {!r.voidedAt && !r.sourcePaymentId && (
                    <Popconfirm
                      title={t('kassa.voidConfirm')}
                      okText={t('kassa.void')}
                      okButtonProps={{ danger: true }}
                      cancelText={t('common.cancel')}
                      onConfirm={() => {
                        void voidTxn.mutateAsync({ id: r.id, reason: t('kassa.voidDefaultReason') })
                          .then(() => void message.success(t('kassa.voided')))
                          .catch((err) => void message.error(asApiError(err).message));
                      }}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}

function AccountFormModal({
  mode, account, onClose,
}: {
  mode: 'create' | 'edit';
  account?: CashAccount;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const create = useCreateCashAccount();
  const update = useUpdateCashAccount();
  const [form] = Form.useForm();
  const kind = Form.useWatch('kind', form) as CashAccountKind | undefined;

  useEffect(() => {
    if (mode === 'edit' && account) {
      form.setFieldsValue({
        name: account.name,
        kind: account.kind,
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        openingBalance: Number(BigInt(account.openingBalance) / 100n),
        isPrimary: account.isPrimary,
        active: account.active,
      });
    }
  }, [mode, account, form]);

  async function onOk() {
    try {
      const v = await form.validateFields();
      const input = {
        ...v,
        openingBalance: v.openingBalance != null ? String(BigInt(Math.round(Number(v.openingBalance) * 100))) : undefined,
      };
      if (mode === 'edit' && account) {
        await update.mutateAsync({ id: account.id, input });
        void message.success(t('kassa.accountUpdated'));
      } else {
        await create.mutateAsync(input);
        void message.success(t('kassa.accountCreated'));
      }
      onClose();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={mode === 'edit' ? t('kassa.editAccount') : t('kassa.newAccount')}
      open
      onOk={onOk}
      onCancel={onClose}
      confirmLoading={create.isPending || update.isPending}
      okText={mode === 'edit' ? t('common.save') : t('common.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ kind: 'cash', currency: 'UZS', isPrimary: false, active: true }}>
        <Form.Item name="name" label={t('kassa.accountName')} rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="kind" label={t('kassa.accountKind')} rules={[{ required: true }]}>
          <Select disabled={mode === 'edit'} options={[{ value: 'cash', label: t('kassa.kinds.cash') }, { value: 'bank', label: t('kassa.kinds.bank') }]} />
        </Form.Item>
        {kind === 'bank' && (
          <Row gutter={12}>
            <Col span={12}><Form.Item name="bankName" label={t('payments.bankAccountBank')}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="accountNumber" label={t('payments.bankAccountNumber')}><Input /></Form.Item></Col>
          </Row>
        )}
        <Form.Item name="openingBalance" label={t('kassa.openingBalance')}>
          <InputNumber<number>
            style={{ width: '100%' }} min={0} step={100000}
            formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
            addonAfter="UZS"
          />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="isPrimary" label={t('kassa.primary')}>
              <Select options={[{ value: true, label: t('common.yes') }, { value: false, label: t('common.no') }]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="active" label={t('users.status')}>
              <Select options={[{ value: true, label: t('users.active') }, { value: false, label: t('users.inactive') }]} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
